<template>
  <div class="tin-root">

    <!-- Splash Overlay -->
    <transition name="splash-fade">
      <div v-if="showSplash" class="splash-overlay">
        <div class="ambient"></div>
        <div class="ambient-floor"></div>
        <div class="grain"></div>
        <div class="frame"></div>
        <div class="frame-inner"></div>

        <div class="splash-body">
          <div class="splash-seal">
            <div class="seal-ring"></div>
            <div class="seal-ring-dashed"></div>
            <div class="seal-diamond"></div>
          </div>

          <div class="splash-wordmark">
            <div class="wm-rule"></div>
            <span class="wm-text">Club Trailz</span>
            <div class="wm-rule r"></div>
          </div>

          <div class="splash-progress-wrap">
            <div class="splash-progress-bar" :style="{ width: loadingProgress + '%' }"></div>
          </div>
          <p class="splash-loading-text">{{ currentLoadingText }}</p>

          <div class="splash-dots">
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </div>
        </div>
      </div>
    </transition>

    <!-- Main Login Screen -->
    <transition name="page-fade">
      <div v-if="!showSplash" class="tin-page">
        <div class="ambient"></div>
        <div class="ambient-floor"></div>
        <div class="grain"></div>
        <div class="frame"></div>
        <div class="frame-inner"></div>

        <!-- Two-column layout wrapper -->
        <div class="page-layout">

          <!-- LEFT COLUMN: What Club Trailz is -->
          <div class="left-col reveal-1">

            <div class="left-seal-wrap">
              <div class="seal">
                <div class="seal-ring"></div>
                <div class="seal-ring-dashed"></div>
                <div class="seal-diamond"></div>
              </div>
              <div class="wordmark-line">
                <div class="wordmark-rule"></div>
                <span class="wordmark-text">Club Trailz</span>
                <div class="wordmark-rule r"></div>
              </div>
            </div>

            <div class="left-headline reveal-2">
              <h1 class="left-h1">Travel intelligence<br>for the reciprocal<br>club world.</h1>
              <div class="left-rule"></div>
            </div>

            <p class="left-premise reveal-3">
              Every serious private club — athletic clubs, golf clubs, city clubs, yacht clubs — operates within a web of reciprocal agreements. Their members are always traveling into cities where they don't know where to eat, stay, or ask for help. Until now, that knowledge lived nowhere but scattered memory.
            </p>

            <p class="left-premise reveal-3">
              Club Trailz is where members tell each other what they know — and where that collective knowledge becomes the case for making it official.
            </p>

            <!-- Three pillars -->
            <div class="pillars reveal-4">

              <div class="pillar">
                <div class="pillar-mark">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3">
                    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.3"/>
                  </svg>
                </div>
                <div class="pillar-body">
                  <span class="pillar-title">City Intelligence</span>
                  <p class="pillar-text">See what fellow reciprocal-club members already recommend in any city — restaurants, hotels, and more — before you land. Post where you're headed and get real answers back.</p>
                </div>
              </div>

              <div class="pillar-divider"></div>

              <div class="pillar">
                <div class="pillar-mark">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3">
                    <path d="M12 2l2.9 6.26L21 9.27l-4.5 4.38L17.8 20 12 16.9 6.2 20l1.3-6.35L3 9.27l6.1-1.01L12 2z"/>
                  </svg>
                </div>
                <div class="pillar-body">
                  <span class="pillar-title">Built From Real Demand</span>
                  <p class="pillar-text">When members ask for an official arrangement somewhere, we see it — and that becomes the case we bring to the business. Not a promise of concierge service already in place. Proof it should be.</p>
                </div>
              </div>

              <div class="pillar-divider"></div>

              <div class="pillar">
                <div class="pillar-mark">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
                  </svg>
                </div>
                <div class="pillar-body">
                  <span class="pillar-title">City Chapters</span>
                  <p class="pillar-text">Every city with member activity has a home base — recommendations, who's traveling there now, and a conversation with the members already on the ground.</p>
                </div>
              </div>

            </div>

            <!-- Trust signal -->
            <div class="trust-block reveal-5">
              <div class="trust-rule"></div>
              <p class="trust-quote">"The reciprocal club world already knows where to eat, stay, and be looked after in every city on earth. It just lives in scattered memory instead of one place. This is where it becomes visible — and where it becomes leverage."</p>
              <div class="trust-rule"></div>
            </div>

            <!-- Pillars of membership -->
            <div class="membership-tags reveal-5">
              <span class="mtag">Verified club affiliation</span>
              <div class="mtag-dot"></div>
              <span class="mtag">Invite-only access</span>
              <div class="mtag-dot"></div>
              <span class="mtag">NDA enforced</span>
              <div class="mtag-dot"></div>
              <span class="mtag">Encrypted</span>
            </div>

          </div>

          <!-- RIGHT COLUMN: Sign-in form -->
          <div class="right-col">

            <div class="container">

              <!-- Headline -->
              <div class="headline-block reveal-2">
                <h2 class="headline">
                  You were<br><em>invited.</em>
                </h2>
                <p class="subline">Enter with the same discretion<br>that brought you here.</p>
              </div>

              <!-- Form -->
              <form class="form reveal-3" @submit.prevent="signInUser">

                <div class="field">
                  <label class="field-label" for="email">Member email</label>
                  <input
                    id="email"
                    v-model.trim="email"
                    class="field-input"
                    type="email"
                    placeholder="your address"
                    autocomplete="email"
                    :disabled="signInProcessing"
                    @input="clearErrors"
                  />
                  <div class="field-bar"></div>
                </div>

                <div class="field">
                  <label class="field-label" for="password">Passphrase</label>
                  <div class="password-wrap">
                    <input
                      id="password"
                      v-model="password"
                      class="field-input"
                      :type="showPassword ? 'text' : 'password'"
                      placeholder="••••••••••••"
                      autocomplete="current-password"
                      :disabled="signInProcessing"
                      @input="clearErrors"
                    />
                    <button
                      type="button"
                      class="pw-toggle"
                      :aria-label="showPassword ? 'Hide passphrase' : 'Show passphrase'"
                      @click="showPassword = !showPassword"
                    >
                      <svg v-if="!showPassword" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                      </svg>
                      <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    </button>
                  </div>
                  <div class="field-bar"></div>
                </div>

                <div class="util-row">
                  <label class="util-label">
                    <input v-model="rememberMe" type="checkbox" />
                    <div class="custom-check"></div>
                    <span class="util-text">Keep me admitted</span>
                  </label>
                  <button
                    class="util-btn"
                    type="button"
                    :disabled="signInProcessing"
                    @click="handleForgotPassword"
                  >
                    Forgot passphrase
                  </button>
                </div>

                <!-- Error message -->
                <transition name="alert-slide">
                  <div v-if="signInErrorMessage" class="error-msg">
                    <span class="error-dot"></span>
                    {{ signInErrorMessage }}
                  </div>
                </transition>

                <!-- Success message -->
                <transition name="alert-slide">
                  <div v-if="infoMessage" class="info-msg">
                    <span class="info-dot"></span>
                    {{ infoMessage }}
                  </div>
                </transition>

                <button
                  class="btn-enter reveal-4"
                  type="submit"
                  :disabled="!canSubmit"
                  :class="{ loading: signInProcessing }"
                >
                  <div class="btn-side"><div class="btn-side-line"></div></div>
                  <div class="btn-center">
                    <div class="btn-tick"></div>
                    <span class="btn-label">
                      {{ signInProcessing ? 'Entering…' : 'Enter the Room' }}
                    </span>
                    <div class="btn-tick"></div>
                  </div>
                  <div class="btn-side r"><div class="btn-side-line"></div></div>
                </button>

              </form>

              <!-- What you get as a member — compact info panel -->
              <div class="member-info-panel reveal-4">
                <div class="mip-header">
                  <div class="mip-diamond"></div>
                  <span class="mip-label">Inside the network</span>
                  <div class="mip-diamond"></div>
                </div>
                <div class="mip-items">
                  <div class="mip-item">
                    <div class="mip-icon">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.3"/>
                      </svg>
                    </div>
                    <span>See what fellow members recommend in any city — restaurants, hotels, and where they've asked for an official arrangement</span>
                  </div>
                  <div class="mip-item">
                    <div class="mip-icon">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                    </div>
                    <span>Post where you're headed, and let members already there give you real recommendations</span>
                  </div>
                  <div class="mip-item">
                    <div class="mip-icon">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                      </svg>
                    </div>
                    <span>Private messaging with peers held to the same standard of vetting you were</span>
                  </div>
                  <div class="mip-item">
                    <div class="mip-icon">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
                      </svg>
                    </div>
                    <span>A city chapter for every place members go — recommendations, who's traveling there, and a real conversation</span>
                  </div>
                  <div class="mip-item">
                    <div class="mip-icon">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                      </svg>
                    </div>
                    <span>A network curated by the standard of the membership, not an algorithm</span>
                  </div>
                </div>
              </div>

              <!-- Waitlist -->
              <div class="divider reveal-5">
                <div class="divider-line"></div>
                <span class="divider-text">Not yet a member?</span>
                <div class="divider-line"></div>
              </div>

              <button class="waitlist-btn reveal-5" type="button" @click="navigateToWaitlist">
                Request an invitation
              </button>

              <!-- Footer -->
              <div class="footer reveal-6">
                <span class="footer-text">Encrypted</span>
                <div class="footer-dot"></div>
                <span class="footer-text">NDA enforced</span>
                <div class="footer-dot"></div>
                <span class="footer-text">Members only</span>
                <div class="footer-dot"></div>
                <span class="footer-text">v2.0</span>
              </div>

            </div>
          </div>

        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db, firebaseApp } from "@/firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const CONFIG = {
  SPLASH_DURATION: 3000,
  // When true, only Admins reach the dashboard and everyone else is bounced
  // to the waitlist — useful for a closed beta wave, but blocks real Members
  // from signing in once you're actually open. Leave false for normal use;
  // flip back to true only if you deliberately want to close intake again.
  LAUNCH_CLOSED: false,
  STORAGE_KEY: 'sc_email',
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  LOADING_PHASES: [
    { text: 'Initialising secure connection…', duration: 800 },
    { text: 'Preparing your experience…', duration: 900 },
    { text: 'Almost ready…', duration: 600 },
  ],
};

const USER_TYPES = { ADMIN: 'Admin', MINISTER: 'Minister', MEMBER: 'Member' };

const ROUTES = {
  HOME: '/home',
  ADMIN_HOME: '/admin-home',
  MINISTER_HOME: '/minister-home',
  WAITLIST_REGISTER: '/waitlist-register',
  WAITLIST_COMPLETE: '/waitlist/complete',
};

export default {
  name: 'Login',

  data() {
    return {
      email: '',
      password: '',
      rememberMe: true,
      showPassword: false,
      signInProcessing: false,
      showSplash: true,
      loadingProgress: 0,
      currentLoadingText: CONFIG.LOADING_PHASES[0].text,
      signInErrorMessage: '',
      infoMessage: '',
    };
  },

  computed: {
    canSubmit() {
      return (
        this.email &&
        this.password &&
        CONFIG.EMAIL_REGEX.test(this.email) &&
        !this.signInProcessing
      );
    },
  },

  mounted() {
    this.animateSplash();
    this.loadSavedEmail();
  },

  methods: {
    animateSplash() {
      this.animateProgress();
      this.cycleLoadingText();
      setTimeout(() => { this.showSplash = false; }, CONFIG.SPLASH_DURATION);
    },

    animateProgress() {
      const duration = CONFIG.SPLASH_DURATION - 200;
      const steps = 60;
      const interval = duration / steps;
      let current = 0;
      const t = setInterval(() => {
        current += 100 / steps;
        this.loadingProgress = Math.min(100, current + (Math.random() * 3 - 1.5));
        if (current >= 100) { this.loadingProgress = 100; clearInterval(t); }
      }, interval);
    },

    cycleLoadingText() {
      let i = 0;
      const next = () => {
        if (i < CONFIG.LOADING_PHASES.length) {
          this.currentLoadingText = CONFIG.LOADING_PHASES[i].text;
          const dur = CONFIG.LOADING_PHASES[i].duration;
          i++;
          if (i < CONFIG.LOADING_PHASES.length) setTimeout(next, dur);
        }
      };
      next();
    },

    loadSavedEmail() {
      try {
        const saved = localStorage.getItem(CONFIG.STORAGE_KEY);
        if (saved && CONFIG.EMAIL_REGEX.test(saved)) {
          this.email = saved;
          this.rememberMe = true;
        }
      } catch (e) { /* silent */ }
    },

    handleEmailPersistence() {
      try {
        if (this.rememberMe && this.email) {
          localStorage.setItem(CONFIG.STORAGE_KEY, this.email);
        } else {
          localStorage.removeItem(CONFIG.STORAGE_KEY);
        }
      } catch (e) { /* silent */ }
    },

    clearErrors() {
      this.signInErrorMessage = '';
      this.infoMessage = '';
    },

    async signInUser() {
      if (!this.canSubmit) return;
      this.signInProcessing = true;
      this.clearErrors();

      try {
        await setPersistence(
          auth,
          this.rememberMe ? browserLocalPersistence : browserSessionPersistence
        );
        const { user } = await signInWithEmailAndPassword(auth, this.email, this.password);
        this.handleEmailPersistence();
        await this.routeUserByType(user.uid);
      } catch (error) {
        this.handleSignInError(error);
      } finally {
        this.signInProcessing = false;
      }
    },

    async routeUserByType(uid) {
      try {
        const userDoc = await getDoc(doc(db, 'user', uid));
        if (userDoc.exists()) {
          await this.handleExistingUser(userDoc, uid);
        } else {
          await this.handleNewUser(uid);
        }
      } catch (error) {
        console.error('Error routing user:', error);
        this.signInErrorMessage = 'Failed to load profile. Please try again.';
      }
    },

    async handleExistingUser(userDoc, uid) {
      const userData = userDoc.data();
      const userType = userData.type || userData.role;

      if (CONFIG.LAUNCH_CLOSED && userType !== USER_TYPES.ADMIN) {
        this.infoMessage = "You're on the waitlist. We'll email you when your wave opens.";
        await this.$nextTick();
        setTimeout(() => this.$router.push(ROUTES.WAITLIST_COMPLETE), 1500);
        return;
      }

      // A not-yet-approved applicant's user doc exists (created at
      // registration) but stays type == 'Applicant' until AdminWaitlist's
      // approve() flips it to 'Member'. Without this check, signing back
      // in before approval fell through to the switch below and hit its
      // default case — a raw "Invalid user type" error — instead of the
      // same friendly waitlist message shown right after registering.
      if (userType === 'Applicant') {
        this.infoMessage = "You're on the waitlist. We'll email you when your wave opens.";
        await this.$nextTick();
        setTimeout(() => this.$router.push(ROUTES.WAITLIST_COMPLETE), 1500);
        return;
      }

      switch (userType) {
        case USER_TYPES.MINISTER:
          this.$router.push(ROUTES.MINISTER_HOME); break;
        case USER_TYPES.ADMIN:
          this.$router.push(ROUTES.ADMIN_HOME); break;
        case USER_TYPES.MEMBER:
          this.$router.push(ROUTES.HOME); break;
        default:
          this.signInErrorMessage = 'Invalid user type. Please contact support.';
      }
    },

    async handleNewUser(uid) {
      const user = auth.currentUser;
      const waitlistDoc = await getDoc(doc(db, 'waitlist', uid));

      if (waitlistDoc.exists()) {
        await this.backfillUserFromWaitlist(uid, user, waitlistDoc);
        this.infoMessage = "You're on the waitlist. We'll email you when your wave opens.";
        await this.$nextTick();
        setTimeout(() => this.$router.push(ROUTES.WAITLIST_COMPLETE), 1500);
      } else {
        this.infoMessage = 'Complete your waitlist registration to get early access.';
        await this.$nextTick();
        setTimeout(() => this.$router.push(ROUTES.WAITLIST_REGISTER), 1500);
      }
    },

    async backfillUserFromWaitlist(uid, user, waitlistDoc) {
      const data = waitlistDoc.data() || {};
      await setDoc(doc(db, 'user', uid), {
        uid,
        fullName: data.fullName || user.displayName || '',
        email: data.email || user.email || '',
        image: null,
        phoneNumber: null,
        username: null,
        role: USER_TYPES.MEMBER,
        type: USER_TYPES.MEMBER,
        createdAt: serverTimestamp(),
        status: 'waitlisted',
      }, { merge: true });
    },

    handleSignInError(error) {
      const msgs = {
        'auth/user-not-found': 'No account found with this email.',
        'auth/wrong-password': 'Incorrect passphrase. Please try again.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/user-disabled': 'This account has been disabled.',
        'auth/too-many-requests': 'Too many attempts. Please try again later.',
        'auth/network-request-failed': 'Network error. Check your connection.',
        'auth/invalid-credential': 'Invalid email or passphrase.',
      };
      this.signInErrorMessage = msgs[error.code] || 'An error occurred. Please try again.';
      console.error('Sign in error:', error);
    },

    async handleForgotPassword() {
      this.clearErrors();
      if (!this.email) {
        this.signInErrorMessage = 'Enter your email above, then click "Forgot passphrase" again.';
        return;
      }
      if (!CONFIG.EMAIL_REGEX.test(this.email)) {
        this.signInErrorMessage = 'Please enter a valid email address.';
        return;
      }
      try {
        // Calls the Cloud Function (index.ts) instead of the client SDK's
        // sendPasswordResetEmail — that sends Firebase's generic,
        // unbranded template and points to Firebase's own hosted reset
        // UI. This sends the same underlying reset link through our own
        // branded email and points to /reset-password instead.
        const functions = getFunctions(firebaseApp);
        const sendReset = httpsCallable(functions, "sendPasswordResetEmail");
        await sendReset({ email: this.email.trim().toLowerCase() });
        // Always the same message regardless of whether an account
        // actually exists for this email — the Cloud Function
        // deliberately returns success either way, so a caller can't
        // enumerate which emails have accounts by watching for a
        // different response.
        this.infoMessage = 'If an account exists for that email, a reset link is on its way — check your inbox and spam folder.';
      } catch (error) {
        // These are HttpsError codes from the Cloud Function
        // (invalid-argument, internal), not Firebase Auth's auth/*
        // codes — the old mapping here was for the client SDK call this
        // replaced and no longer matched anything real.
        const msgs = {
          'invalid-argument': 'Please enter a valid email address.',
        };
        this.signInErrorMessage = msgs[error.code] || 'Failed to send reset email. Please try again.';
      }
    },

    navigateToWaitlist() {
      this.$router.push(ROUTES.WAITLIST_REGISTER);
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400;1,500&family=Montserrat:wght@300;400;500;600&display=swap');

/* ── RESET & ROOT ── */
.tin-root {
  position: relative;
  min-height: 100vh;
  width: 100%;
}

/* ── SHARED LAYERS ── */
.ambient {
  position: fixed;
  top: -300px;
  left: 50%;
  transform: translateX(-50%);
  width: 1000px;
  height: 800px;
  background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.11) 0%, rgba(210,175,100,0.04) 30%, transparent 65%);
  pointer-events: none;
  z-index: 0;
}

.ambient-floor {
  position: fixed;
  bottom: -200px;
  left: 50%;
  transform: translateX(-50%);
  width: 700px;
  height: 400px;
  background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.04) 0%, transparent 60%);
  pointer-events: none;
  z-index: 0;
}

.grain {
  position: fixed;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  opacity: 0.55;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E");
  background-size: 180px 180px;
}

.frame {
  position: fixed;
  inset: 28px;
  border: 1px solid rgba(210,175,100,0.08);
  z-index: 2;
  pointer-events: none;
}
.frame::before, .frame::after {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
}
.frame::before {
  top: -1px; left: -1px;
  border-top: 1px solid rgba(210,175,100,0.5);
  border-left: 1px solid rgba(210,175,100,0.5);
}
.frame::after {
  bottom: -1px; right: -1px;
  border-bottom: 1px solid rgba(210,175,100,0.5);
  border-right: 1px solid rgba(210,175,100,0.5);
}

.frame-inner {
  position: fixed;
  inset: 28px;
  z-index: 2;
  pointer-events: none;
}
.frame-inner::before, .frame-inner::after {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
}
.frame-inner::before {
  top: -1px; right: -1px;
  border-top: 1px solid rgba(210,175,100,0.5);
  border-right: 1px solid rgba(210,175,100,0.5);
}
.frame-inner::after {
  bottom: -1px; left: -1px;
  border-bottom: 1px solid rgba(210,175,100,0.5);
  border-left: 1px solid rgba(210,175,100,0.5);
}

/* ── SPLASH ── */
.splash-overlay {
  position: fixed;
  inset: 0;
  background: #070708;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.splash-body {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  width: 320px;
}

.splash-seal {
  position: relative;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;
}

.splash-wordmark {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  margin-bottom: 40px;
}

.wm-rule {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(210,175,100,0.25));
}
.wm-rule.r {
  background: linear-gradient(90deg, rgba(210,175,100,0.25), transparent);
}
.wm-text {
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 3.5px;
  color: rgba(210,175,100,0.5);
  text-transform: uppercase;
  white-space: nowrap;
}

.splash-progress-wrap {
  width: 100%;
  height: 1px;
  background: rgba(237,232,223,0.07);
  margin-bottom: 16px;
  position: relative;
  overflow: hidden;
}

.splash-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, rgba(210,175,100,0.6), rgba(210,175,100,0.3));
  transition: width 0.35s cubic-bezier(0.4,0,0.2,1);
}

.splash-loading-text {
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 2px;
  color: rgba(237,232,223,0.25);
  text-transform: uppercase;
  margin-bottom: 32px;
  min-height: 18px;
}

.splash-dots {
  display: flex;
  gap: 8px;
}

.dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(210,175,100,0.4);
  animation: bounceDot 1.4s ease-in-out infinite;
}
.dot:nth-child(2) { animation-delay: 0.2s; }
.dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes bounceDot {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-8px); opacity: 1; }
}

/* ── MAIN PAGE ── */
.tin-page {
  position: relative;
  min-height: 100vh;
  background: #070708;
  display: flex;
  align-items: stretch;
}

/* ── TWO-COLUMN LAYOUT ── */
.page-layout {
  position: relative;
  z-index: 10;
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  min-height: 100vh;
}

/* ── LEFT COLUMN ── */
.left-col {
  padding: 80px 64px 80px 80px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(210,175,100,0.08);
  position: relative;
}

.left-col::after {
  content: '';
  position: absolute;
  top: 0; right: 0; bottom: 0;
  width: 1px;
  background: linear-gradient(180deg,
    transparent 0%,
    rgba(210,175,100,0.15) 20%,
    rgba(210,175,100,0.15) 80%,
    transparent 100%
  );
}

.left-seal-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 52px;
  width: 100%;
}

.seal {
  position: relative;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.seal-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid rgba(210,175,100,0.2);
  animation: spinSlow 40s linear infinite;
}
.seal-ring-dashed {
  position: absolute;
  inset: 6px;
  border-radius: 50%;
  border: 1px dashed rgba(210,175,100,0.15);
  animation: spinSlow 25s linear infinite reverse;
}
@keyframes spinSlow { to { transform: rotate(360deg); } }

.seal-diamond {
  width: 14px;
  height: 14px;
  border: 1px solid rgba(210,175,100,0.7);
  transform: rotate(45deg);
  position: relative;
  z-index: 1;
}
.seal-diamond::after {
  content: '';
  position: absolute;
  inset: 3px;
  background: rgba(210,175,100,0.35);
}

.wordmark-line {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
}
.wordmark-rule {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18));
}
.wordmark-rule.r {
  background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent);
}
.wordmark-text {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 4px;
  color: rgba(210,175,100,0.45);
  text-transform: uppercase;
  white-space: nowrap;
}

/* Left headline */
.left-headline {
  margin-bottom: 32px;
}
.left-h1 {
  font-family: 'Cormorant', serif;
  font-size: 48px;
  font-weight: 300;
  line-height: 1.15;
  color: #edeae2;
  letter-spacing: -0.5px;
  margin-bottom: 28px;
}
.left-rule {
  width: 40px;
  height: 1px;
  background: rgba(210,175,100,0.4);
}

/* Left premise text */
.left-premise {
  font-family: 'Cormorant', serif;
  font-size: 21px;
  font-style: italic;
  font-weight: 300;
  line-height: 1.7;
  color: rgba(237,232,223,0.45);
  margin-bottom: 16px;
  letter-spacing: 0.2px;
}

/* Pillars */
.pillars {
  margin-top: 40px;
  margin-bottom: 44px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.pillar {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding: 24px 0;
}

.pillar-divider {
  height: 1px;
  width: 100%;
  background: linear-gradient(90deg, rgba(210,175,100,0.12), transparent);
}

.pillar-mark {
  width: 32px;
  height: 32px;
  border: 1px solid rgba(210,175,100,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(210,175,100,0.6);
  flex-shrink: 0;
  margin-top: 2px;
}

.pillar-body {
  flex: 1;
}

.pillar-title {
  display: block;
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: rgba(210,175,100,0.7);
  margin-bottom: 8px;
}

.pillar-text {
  font-family: 'Cormorant', serif;
  font-size: 19px;
  font-weight: 300;
  font-style: italic;
  line-height: 1.65;
  color: rgba(237,232,223,0.38);
  margin: 0;
}

/* Trust block */
.trust-block {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 36px;
}

.trust-rule {
  height: 1px;
  background: linear-gradient(90deg, rgba(210,175,100,0.15), transparent);
}

.trust-quote {
  font-family: 'Cormorant', serif;
  font-size: 19px;
  font-style: italic;
  font-weight: 300;
  line-height: 1.75;
  color: rgba(237,232,223,0.28);
  margin: 0;
  letter-spacing: 0.2px;
}

/* Membership tags */
.membership-tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: auto;
}

.mtag {
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: rgba(237,232,223,0.18);
}

.mtag-dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: rgba(210,175,100,0.2);
}

/* ── RIGHT COLUMN ── */
.right-col {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 0;
  overflow-y: auto;
}

/* ── CONTAINER (right column inner) ── */
.container {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 480px;
  padding: 80px 56px 80px 64px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ── TRANSITIONS ── */
.splash-fade-leave-active {
  transition: opacity 0.8s ease;
}
.splash-fade-leave-to { opacity: 0; }

.page-fade-enter-active {
  transition: opacity 0.6s ease 0.2s;
}
.page-fade-enter { opacity: 0; }

.alert-slide-enter-active, .alert-slide-leave-active {
  transition: all 0.3s ease;
}
.alert-slide-enter, .alert-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ── STAGGER REVEALS ── */
.reveal-1 { animation: fadeUp 1.0s cubic-bezier(0.16,1,0.3,1) 0.1s both; }
.reveal-2 { animation: fadeUp 1.0s cubic-bezier(0.16,1,0.3,1) 0.25s both; }
.reveal-3 { animation: fadeUp 1.0s cubic-bezier(0.16,1,0.3,1) 0.4s both; }
.reveal-4 { animation: fadeUp 1.0s cubic-bezier(0.16,1,0.3,1) 0.55s both; }
.reveal-5 { animation: fadeUp 1.0s cubic-bezier(0.16,1,0.3,1) 0.7s both; }
.reveal-6 { animation: fadeUp 1.0s cubic-bezier(0.16,1,0.3,1) 0.85s both; }

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── HEADLINE ── */
.headline-block {
  text-align: center;
  margin-bottom: 52px;
  width: 100%;
}
.headline {
  font-family: 'Cormorant', serif;
  font-size: 68px;
  font-weight: 300;
  line-height: 1.0;
  color: #edeae2;
  letter-spacing: -1.5px;
  margin-bottom: 4px;
}
.headline em {
  font-style: italic;
  display: block;
  color: rgba(210,175,100,0.85);
  font-weight: 300;
}
.subline {
  font-family: 'Cormorant', serif;
  font-size: 20px;
  font-style: italic;
  font-weight: 300;
  color: rgba(237,232,223,0.35);
  letter-spacing: 0.3px;
  margin-top: 14px;
  line-height: 1.55;
}

/* ── FORM ── */
.form { width: 100%; margin-bottom: 36px; }

.field {
  position: relative;
  margin-bottom: 32px;
}
.field-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 3px;
  color: rgba(210,175,100,0.38);
  text-transform: uppercase;
  margin-bottom: 10px;
  transition: color 0.35s;
}
.field:focus-within .field-label { color: rgba(210,175,100,0.75); }

.password-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.pw-toggle {
  position: absolute;
  right: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(210,175,100,0.3);
  padding: 4px;
  display: flex;
  align-items: center;
  transition: color 0.2s;
}
.pw-toggle:hover { color: rgba(210,175,100,0.7); }

.field-input {
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(237,232,223,0.09);
  padding: 14px 32px 14px 0;
  color: #edeae2;
  font-family: 'Montserrat', sans-serif;
  font-size: 20px;
  font-weight: 300;
  letter-spacing: 0.3px;
  outline: none;
  caret-color: rgba(210,175,100,0.8);
  transition: border-color 0.35s;
}
.field-input::placeholder { color: rgba(237,232,223,0.13); font-style: italic; }
.field-input:focus { border-bottom-color: rgba(210,175,100,0.22); }
.field-input:disabled { opacity: 0.5; cursor: not-allowed; }

.field-bar {
  position: absolute;
  bottom: 0; left: 0;
  height: 1px; width: 0;
  background: linear-gradient(90deg, rgba(210,175,100,0.8), rgba(210,175,100,0.2));
  transition: width 0.55s cubic-bezier(0.25,0.46,0.45,0.94);
  pointer-events: none;
}
.field:focus-within .field-bar { width: 100%; }

/* Util row */
.util-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
}
.util-label {
  display: flex;
  align-items: center;
  gap: 9px;
  cursor: pointer;
}
.util-label input { display: none; }
.custom-check {
  width: 15px; height: 15px;
  border: 1px solid rgba(210,175,100,0.3);
  position: relative;
  flex-shrink: 0;
}
.custom-check::after {
  content: '';
  position: absolute;
  inset: 3px;
  background: rgba(210,175,100,0.75);
  opacity: 0;
  transition: opacity 0.2s;
}
.util-label input:checked ~ .custom-check::after { opacity: 1; }
.util-text {
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 1.5px;
  color: rgba(237,232,223,0.28);
  text-transform: uppercase;
}
.util-btn {
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 1.5px;
  color: rgba(210,175,100,0.38);
  text-transform: uppercase;
  background: none; border: none; cursor: pointer;
  transition: color 0.25s;
  font-family: 'Montserrat', sans-serif;
}
.util-btn:hover { color: rgba(210,175,100,0.8); }
.util-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* Messages */
.error-msg, .info-msg {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.3px;
  padding: 10px 0;
  margin-bottom: 16px;
  border-bottom: 1px solid rgba(237,232,223,0.06);
}
.error-msg { color: rgba(220,100,90,0.8); }
.info-msg  { color: rgba(210,175,100,0.7); }
.error-dot {
  width: 4px; height: 4px;
  border-radius: 50%;
  background: rgba(220,100,90,0.8);
  flex-shrink: 0;
}
.info-dot {
  width: 4px; height: 4px;
  border-radius: 50%;
  background: rgba(210,175,100,0.7);
  flex-shrink: 0;
}

/* ── ENTER BUTTON ── */
.btn-enter {
  width: 100%;
  background: transparent;
  border: 1px solid rgba(210,175,100,0.28);
  padding: 0;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  height: 72px;
  transition: border-color 0.4s, opacity 0.2s;
}
.btn-enter::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(210,175,100,0.065);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94);
}
.btn-enter:not(:disabled):hover { border-color: rgba(210,175,100,0.6); }
.btn-enter:not(:disabled):hover::before { transform: scaleX(1); }
.btn-enter:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-enter.loading { pointer-events: none; }

.btn-side {
  width: 56px; height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid rgba(210,175,100,0.15);
  flex-shrink: 0;
  position: relative; z-index: 1;
  transition: border-color 0.4s;
}
.btn-side.r {
  border-right: none;
  border-left: 1px solid rgba(210,175,100,0.15);
}
.btn-enter:hover .btn-side { border-color: rgba(210,175,100,0.3); }

.btn-side-line { width: 1px; height: 20px; background: rgba(210,175,100,0.3); }

.btn-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  position: relative; z-index: 1;
}
.btn-label {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 4px;
  color: rgba(210,175,100,0.8);
  text-transform: uppercase;
}
.btn-tick {
  width: 20px; height: 1px;
  background: rgba(210,175,100,0.35);
  transition: width 0.35s;
  flex-shrink: 0;
}
.btn-enter:hover .btn-tick { width: 28px; }

/* ── MEMBER INFO PANEL ── */
.member-info-panel {
  width: 100%;
  border: 1px solid rgba(210,175,100,0.1);
  background: rgba(210,175,100,0.025);
  padding: 28px 28px 24px;
  margin-top: 32px;
  position: relative;
}

.member-info-panel::before,
.member-info-panel::after {
  content: '';
  position: absolute;
  width: 12px;
  height: 12px;
}
.member-info-panel::before {
  top: -1px; left: -1px;
  border-top: 1px solid rgba(210,175,100,0.35);
  border-left: 1px solid rgba(210,175,100,0.35);
}
.member-info-panel::after {
  bottom: -1px; right: -1px;
  border-bottom: 1px solid rgba(210,175,100,0.35);
  border-right: 1px solid rgba(210,175,100,0.35);
}

.mip-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 22px;
}

.mip-diamond {
  width: 6px;
  height: 6px;
  border: 1px solid rgba(210,175,100,0.4);
  transform: rotate(45deg);
  flex-shrink: 0;
}

.mip-label {
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: rgba(210,175,100,0.4);
  white-space: nowrap;
}

.mip-items {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.mip-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(237,232,223,0.04);
}

.mip-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.mip-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(210,175,100,0.4);
  flex-shrink: 0;
  margin-top: 1px;
}

.mip-item span {
  font-family: 'Cormorant', serif;
  font-size: 17px;
  font-weight: 300;
  font-style: italic;
  line-height: 1.6;
  color: rgba(237,232,223,0.38);
  letter-spacing: 0.2px;
}

/* ── DIVIDER ── */
.divider {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  margin: 28px 0;
}
.divider-line { flex: 1; height: 1px; background: rgba(237,232,223,0.06); }
.divider-text {
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 2px;
  color: rgba(237,232,223,0.18);
  text-transform: uppercase;
  white-space: nowrap;
}

/* ── WAITLIST ── */
.waitlist-btn {
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 2.5px;
  color: rgba(237,232,223,0.3);
  text-transform: uppercase;
  background: none; border: none; cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  transition: color 0.25s;
  padding: 4px 0;
  position: relative;
}
.waitlist-btn::after {
  content: '';
  position: absolute;
  bottom: 0; left: 50%; right: 50%;
  height: 1px;
  background: rgba(210,175,100,0.35);
  transition: left 0.3s ease, right 0.3s ease;
}
.waitlist-btn:hover { color: rgba(237,232,223,0.5); }
.waitlist-btn:hover::after { left: 0; right: 0; }

/* ── FOOTER ── */
.footer {
  margin-top: 48px;
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  justify-content: center;
}
.footer-dot {
  width: 3px; height: 3px;
  border-radius: 50%;
  background: rgba(210,175,100,0.25);
  animation: pulseDot 3.5s ease-in-out infinite;
}
.footer-dot:nth-child(2) { animation-delay: 1.2s; }
.footer-dot:nth-child(4) { animation-delay: 2.4s; }
@keyframes pulseDot {
  0%, 100% { opacity: 0.25; }
  50% { opacity: 0.7; }
}
.footer-text {
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 1.8px;
  color: rgba(237,232,223,0.2);
  text-transform: uppercase;
}

/* ── RESPONSIVE ── */
@media (max-width: 960px) {
  .page-layout {
    grid-template-columns: 1fr;
  }
  .left-col {
    padding: 60px 40px 48px;
    border-right: none;
    border-bottom: 1px solid rgba(210,175,100,0.08);
  }
  .left-col::after { display: none; }
  .left-h1 { font-size: 40px; }
  .right-col { justify-content: center; }
  .container { padding: 60px 40px; max-width: 520px; }
  .headline { font-size: 58px; }
}

@media (max-width: 500px) {
  .frame { inset: 16px; }
  .frame-inner { inset: 16px; }
  .left-col { padding: 48px 24px 40px; }
  .container { padding: 48px 24px; }
  .headline { font-size: 46px; }
  .subline { font-size: 18px; }
  .btn-side { width: 40px; }
  .membership-tags { gap: 8px; }
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}
</style>